import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { closeModal, popModal } from 'loot-core/client/actions';

import { useDemoSite } from '../../hooks/useDemoSite';
import { AsyncStorageKey, useStorage } from '../../hooks/useStorage';
import ForgotPasswordForm from '../auth/login/ForgotPasswordForm';
import { LoginForm } from '../auth/login/LoginForm';
import { RemoteSignupForm } from '../auth/remote-signup/RemoteSignupForm';
import { Button } from '../common/Button';
import { Modal, ModalButtons } from '../common/Modal';
import { Text } from '../common/Text';
import { type CommonModalProps } from '../Modals';

type HybridAuthModalProps = {
  modalProps: CommonModalProps;
  type?: 'login' | 'signup' | 'forgot-password';
  onLogin?: () => void;
  onModalClose?: () => void;
};

export function HybridAuthModal({
  modalProps,
  type: _type,
  onLogin,
  onModalClose,
}: HybridAuthModalProps) {
  const dispatch = useDispatch();
  const { getItem: getUserData } = useStorage(AsyncStorageKey.USER_DATA);
  const [initialEmail, setInitialEmail] = useState('');
  const [type, setType] = useState(_type || 'signup');
  const { isDemoBudgetFile } = useDemoSite();

  useEffect(() => {
    (async () => {
      if (type === 'signup') {
        const userData = await getUserData();
        setInitialEmail(userData.email);
      }
    })();
  }, []);

  function switchTypeToLogin() {
    setType('login');
  }

  function switchTypeToSignup() {
    setType('signup');
  }

  function switchTypeToForgotPassword() {
    setType('forgot-password');
  }

  function _onModalClose() {
    if (onModalClose) {
      dispatch(popModal());
      onModalClose();
    } else {
      modalProps.onClose();
    }
  }

  function getModalTitleByType() {
    switch (type) {
      case 'forgot-password':
        return 'Password recovery';
      case 'signup':
        return 'Signup';
      case 'login':
        return 'Sign-in';
    }
  }

  function renderAuthForm() {
    switch (type) {
      case 'login':
        return (
          <LoginForm
            onLogin={onLogin}
            initialEmail={initialEmail}
            switchToSignup={switchTypeToSignup}
            switchTypeToForgotPassword={switchTypeToForgotPassword}
          />
        );
      case 'signup':
        return (
          <RemoteSignupForm
            onSignup={onLogin}
            initialEmail={initialEmail}
            switchToLogin={switchTypeToLogin}
          />
        );
      case 'forgot-password':
        return <ForgotPasswordForm onSwitchToSignin={switchTypeToLogin} />;
    }
  }

  function renderDemoWarning() {
    return (
      <>
        <Text>
          Signing up in the demo environment is not possible. Please use{' '}
          <a href="https://app.goodsteward.io/signup">this link</a> to sign up
          and start using the app.
        </Text>
        <ModalButtons>
          <Button type="primary" onClick={() => dispatch(closeModal())}>
            Understood
          </Button>
        </ModalButtons>
      </>
    );
  }

  return (
    <Modal
      title={getModalTitleByType()}
      {...modalProps}
      onClose={_onModalClose}
      style={{ maxWidth: 400 }}
    >
      {isDemoBudgetFile ? renderDemoWarning() : renderAuthForm()}
    </Modal>
  );
}
